import { useContext, useEffect, useState } from "react"
import { UpgradeModalContext } from "Hooks/UpgradeModalContext"
import AddUserModal from "./AddUserModal"
import { useHistory } from "react-router-dom"
import { OrganizationTab } from "../../OrganizationPage/Organization_constants"
import { BrandUsersApiResponse } from "api/brandApi"
import useBrand from 'Hooks/useBrand'
import Sty from "modules/Sty"
import { PlanType, PRO_PLAN_ADDITIONAL_SEAT_PRICE, UserPermissionOnBrand, USER_PERMISSION_ON_BRAND_INFO, IS_TEAM_PLAN_UPGRADE_ENABLED } from "modules/Const"
import ButtonSymphony from "elements/ButtonSymphony"
import Colors from "modules/Colors"
import ModalSymphony from "elements/ModalSymphony"
import FeatureGate from "components/FeatureGate"
import Banner from "elements/Banner"
import Loader from "elements/Loader"
import { toastError, toastSuccess } from "modules/Toast"
import useUser from "Hooks/useUser"
import TableElement, { ColDef } from "elements/TableElement"
import DebugBox from "components/shareable/DebugBox"
import { capitalizeFirstLetter } from "modules/Utils"
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony"
import Icon from "elements/Icon"

import useOrganizationV2 from "Hooks/useOrganizationV2"
import useScreen from "Hooks/useScreen"
import { OrganizationStatus, OrganizationInvitationStatus } from "modules/Types"
import PageSymphony from "elements/PageSymphony"
import CardSymphony from "elements/CardSymphony"
import AddMemberModal from "pages/OrganizationPage/componentsOrganization/AddMemberModal/AddMemberModal"
import EditBrandUserPermissionsModal from "pages/OrganizationPage/componentsOrganization/EditBrandUserPermissionsModal/EditBrandUserPermissionsModal"
import useAppView from "Hooks/useAppView"
import { useViewOnlyAccess, ViewOnlyFeature } from "Hooks/useViewOnlyAccess"
import { FeatureKeys } from "features/definitions"

interface MembersPageProps {
    className: string;
    brandBelongsToOrganization?: boolean;
    onClickCreateTeam?: () => void;
}

const MembersPage = ({
    className,
    brandBelongsToOrganization,
    onClickCreateTeam,
}: MembersPageProps) => {
    const history = useHistory();
    const {
        mobileView,
    } = useScreen()

    const { currentUser } = useUser()
    const {
        isProBrand,
        deleteUserFromBrand,
        getBrandUsers,
        currentBrand,
        hasViewOnlyTeamBrands,
    } = useBrand()

    const { 
        organization, 
        organizationUsers, 
        hasOrganizationAdminAccess,
        getSelectedBrand 
    } = useOrganizationV2()
    const { isBrandView } = useAppView()
    
    // Check View Only access for team features
    const { hasAccess: canViewTeamMembers } = useViewOnlyAccess(ViewOnlyFeature.TEAM_VIEW_MEMBERS)
    const { hasAccess: canInviteMembers } = useViewOnlyAccess(ViewOnlyFeature.TEAM_INVITE_MEMBER)

    const { handleOpenModal: openUpgradeToProModal } = useContext(UpgradeModalContext)

    const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false)
    const [showAddMemberModal, setShowAddMemberModal] = useState<boolean>(false)
    const [showDeleteUserModal, setShowDeleteUserModal] = useState<boolean>(false)
    const [showEditBrandPermissionsModal, setShowEditBrandPermissionsModal] = useState<boolean>(false)
    const [isLoadingBrandUsers, setIsLoadingBrandUsers] = useState<boolean>(false)

    const [pulledUsers, setPulledUsers] = useState<BrandUsersApiResponse>({ availableSeats: 0, users: [] })
    const [isHandlingEditAction, setIsHandlingEditAction] = useState<boolean>(false)

    // user to update
    const [userToEdit, setUserToEdit] = useState<any | null>(null)

    // Context detection - determine if we should show team users for this brand
    const isBrandInOrganization = !!(organization && currentBrand && currentBrand.organizationId === organization.id)
    const isTeamContextForBrand = !!(organization && currentBrand && isBrandView() && isBrandInOrganization)
    const shouldShowTeamUsers = isTeamContextForBrand
    const shouldHideStartTeamButton = !!organization || hasViewOnlyTeamBrands()

    const fetchUsers = async () => {
        setIsLoadingBrandUsers(true)
        try {
            const { users, availableSeats } = await getBrandUsers()
            setPulledUsers({ users, availableSeats })
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoadingBrandUsers(false)
        }
    }

    // Get users to display based on context
    const getUsersToDisplay = () => {
        if (shouldShowTeamUsers && organizationUsers && currentBrand) {
            // Show organization users who have access to current brand
            const teamUsersWithBrandAccess = organizationUsers
                .filter(orgUser => {
                    // Include users who have brand access
                    const hasBrandAccess = orgUser.brands?.some((brand: any) => 
                        brand.id === currentBrand.id && brand.permissionLevel !== 'none'
                    )
                    
                    // Also include pending invitations - when someone is invited to a specific brand
                    // from brand view, they should show up immediately even if brand association 
                    // isn't fully set up yet in the response
                    const isPendingInvitation = orgUser.invitation_status === 'pending'
                    
                    return hasBrandAccess || isPendingInvitation
                })
                .map(orgUser => {
                    // Transform to match BrandUser interface
                    const brandPermission = orgUser.brands?.find((brand: any) => brand.id === currentBrand.id)
                    const isOwner = brandPermission?.permissionLevel === 'owner'
                    const permissionLevel = brandPermission?.permissionLevel || 'none'
                    
                    return {
                        id: orgUser.user_id,
                        email: orgUser.email,
                        name: orgUser.name,
                        isOwner,
                        permissionLevel,
                        invitation_status: orgUser.invitation_status // Pass through invitation status
                    }
                })
            
            return {
                users: teamUsersWithBrandAccess,
                availableSeats: 0 // Not relevant for team context
            }
        }
        
        // Default to brand users
        return pulledUsers
    }

    useEffect(() => {
        fetchUsers()
    }, [currentBrand])

    // Note: When in team context (shouldShowTeamUsers=true), the component automatically 
    // re-renders when organizationUsers changes due to Zustand subscription

    const handleCloseAddModal = () => {
        setShowAddUserModal(false)
    }

    const handleCloseDeleteModal = async () => {
        setUserToEdit(null)
        await fetchUsers()
        setShowDeleteUserModal(false)
    }

    const handleClickAddNewMemberButton = () => {
        // If we're in team context (organization + brand view), use AddMemberModal
        if (isTeamContextForBrand) {
            setShowAddMemberModal(true)
        } 
        // Legacy brand-belongs-to-organization redirect (keeping for backward compatibility)
        else if (brandBelongsToOrganization) {
            history.push(`/team?tab=${OrganizationTab.TEAM}`)
        } 
        // Brand-only context
        else {
            if (isProBrand) {
                setShowAddUserModal(true)
            } else {
                openUpgradeToProModal({
                    source: "Settings - Add New User",
                    highlightedPlan: PlanType.PRO,
                    upgradeCallToAction: "Invite your team with Symphony Pro",
                    onlyShowPlans: [PlanType.PRO]
                })
            }
        }
    }

    const handleStartTeamAccountButton = () => {
        openUpgradeToProModal({
            source: "Settings - Start Team Account",
            highlightedPlan: PlanType.TEAM_V2,
            onlyShowPlans: [PlanType.TEAM_V2],
            upgradeCallToAction: "Start your team account with Symphony Team",
        })
    }

    const handleDeleteUser = async () => {
        setIsHandlingEditAction(true)
        try {
            const { users, availableSeats } = await deleteUserFromBrand({
                id: userToEdit?.id!,
                email: userToEdit?.email!
            })
            setPulledUsers({ users, availableSeats })
        } catch (error) {
            console.error(error)
            toastError(`There was an error deleting ${userToEdit?.email} from your profile - please try again.`)
        } finally {
            toastSuccess(`${userToEdit?.email} has been removed from your profile.`)
            setShowDeleteUserModal(false)
            setIsHandlingEditAction(false)
        }
    }

    const renderSubscriptionChangeNotice = () => {
        if (pulledUsers.users?.length <= 2) {
            return null
        }

        return (
            <Banner
                className="deleteUserModalBanner"
                color="purple"
                title="Your monthly subscription will be reduced"
                description={`Since you're removing an extra user, your next invoice will be reduced by $${PRO_PLAN_ADDITIONAL_SEAT_PRICE}`}
            />
        )
    }

    const renderDeleteUserModal = () => {
        return (
            <ModalSymphony
                className="deleteUserModalSettingsPage"
                style={{ gap: 16 }}
                isOpen={showDeleteUserModal}
                onClose={handleCloseDeleteModal}
                title="Remove member"
                subtitle="Are you sure you want to remove this member?"

                onClickButtonLeft={handleCloseDeleteModal}
                buttonLeftText="Cancel"
                buttonLeftDisabled={isHandlingEditAction}

                onClickButtonRight={handleDeleteUser}
                buttonRightText="Remove member"
                buttonRightColor="red"
                buttonRightIsLoading={isHandlingEditAction}
                buttonRightDisabled={isHandlingEditAction}
            >
                <div
                    className="deleteUserModalSettingsPageEmail"
                    style={Sty.text18bold}
                >
                    {userToEdit?.email}
                </div>
            </ModalSymphony>
        )
    }

    const renderAddUserModal = () => {
        return (
            <AddUserModal
                handleUpdateUsers={(users) => {
                    setPulledUsers({ users, availableSeats: pulledUsers.availableSeats })
                }}
                open={showAddUserModal}
                onClose={handleCloseAddModal}
                currentUserCount={pulledUsers.users?.length}
            />
        )
    }

    const renderUsersTable = () => {
        const displayData = getUsersToDisplay()
        const isLoading = isLoadingBrandUsers || (shouldShowTeamUsers && !organizationUsers)
        
        if (isLoading || !displayData.users) {
            return (
                <Loader className="usersListLoader" title="Loading Users..." style={{ marginTop: 24 }} />
            )
        }

        const columnDefs: ColDef[] = [
            {
                field: 'email',
                title: 'Email',
                flex: mobileView ? 0 : 1,
                width: mobileView ? 260 : undefined,
            },
            {
                field: 'permissionLevel',
                title: 'Role',
                flex: mobileView ? 0 : 1,
                width: mobileView ? 120 : undefined,
                renderCell: (row: any) => {
                    const isOwner = row.permissionLevel === 'owner'
                    const hasNoAccess = row.permissionLevel === 'none'
                    const isPending = row.invitation_status === 'pending'

                    let displayTitle = '';
                    if (hasNoAccess) {
                        displayTitle = 'No Access';
                    } else {
                        displayTitle = USER_PERMISSION_ON_BRAND_INFO[row.permissionLevel as UserPermissionOnBrand]?.title || row.permissionLevel;
                    }

                    // Add pending status for invitations
                    if (isPending) {
                        displayTitle += ' (Invited)';
                    }

                    return (
                        <div
                            className="userRoleText"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                color: hasNoAccess ? Colors.greyDark : Colors.greyDark
                            }}
                        >
                            <div>{displayTitle}</div>
                            {isOwner &&
                                <Icon
                                    className="userRoleIcon"
                                    icon="crown"
                                    color={Colors.gold}
                                />
                            }
                        </div>
                    )

                    // TODO CONNER: uncomment this when we actual use the real team plan
                    // return (
                    //     <InputSymphony
                    //         className="userRoleInput"
                    //         maxWidth={160}
                    //         value={capitalizeFirstLetter(permissionLevel)}
                    //         isSelectDropdown
                    //         options={[
                    //             { title: 'Admin', value: 'administrator' },
                    //             { title: 'Member', value: 'member' }
                    //         ] as SelectOptionItem[]}
                    //         onChange={(e) => {
                    //             console.log(e)
                    //         }}
                    //         disabled={true}
                    //         ignoreHorizontalScrollPositioning={false}
                    //     />
                    // )
                },
            },
            {
                field: 'removable',
                title: 'Actions',
                flex: mobileView ? 0 : 1,
                width: mobileView ? 120 : undefined,
                renderCell: (row: any) => {
                    if (!row.removable) {
                        return (
                            <div>
                                -
                            </div>
                        )
                    }

                    // In team context, show Edit Permissions button instead of Remove
                    if (shouldShowTeamUsers && hasOrganizationAdminAccess()) {
                        return (
                            <ButtonSymphony
                                className="userEditPermissionsButton"
                                onClick={() => {
                                    setUserToEdit(row);
                                    setShowEditBrandPermissionsModal(true);
                                }}
                                variant="text"
                            >
                                Edit Permissions
                            </ButtonSymphony>
                        );
                    }



                    // Default brand context - show Remove button
                    return (
                        <ButtonSymphony
                            className="userRemoveButton"
                            onClick={() => {
                                setUserToEdit(row);
                                setShowDeleteUserModal(true);
                            }}
                            variant="text"
                            color="red"
                        >
                            Remove
                        </ButtonSymphony>
                    );
                },
            }
        ];

        const rows = displayData.users.map(({
            email,
            id,
            name,
            isOwner,
            permissionLevel
        }) => {
            const isRemovable: boolean = currentUser?.email !== email && permissionLevel !== 'owner';
            return {
                id,
                email,
                isOwner,
                permissionLevel,
                name: name || '-',
                removable: isRemovable
            };
        }).sort((a, b) => {
            // Sort by permission priority using the defined priority values
            const getPriority = (permission: string) => {
                return USER_PERMISSION_ON_BRAND_INFO[permission as UserPermissionOnBrand]?.priority ?? 999;
            };

            const aPriority = getPriority(a.permissionLevel ?? '');
            const bPriority = getPriority(b.permissionLevel ?? '');

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            // If same permission level, sort alphabetically by email
            return a.email?.localeCompare(b.email!) ?? 0;
        });

        return (
            <TableElement
                className="usersTable"
                rowData={rows}
                columnDefs={columnDefs}
                pagination={false}
                rowsTitle="users"
                isHover={false}
            />
        )
    }

    const renderUpsellToTeamPlanBanner = () => {
        if (organization?.setup_status !== OrganizationStatus.READY) {
            return null
        }

        return (
            <Banner
                className="membersPageUpgradeToProBanner"
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16 }}
            >
                <Icon
                    className="membersPageUpgradeToProIcon"
                    icon="audiences"
                    size={32}
                />
                <div className="membersPageUpgradeToProText">
                    <div className="membersPageUpgradeToProTextTitle" style={Sty.text20purple}>
                        Unlock discounted pricing with Symphony for Teams
                    </div>
                    <div className="membersPageUpgradeToProTextSubtitle" style={Sty.text16grey}>
                        Save money + time with a Team Account
                    </div>
                </div>
                <ButtonSymphony
                    className="membersPageUpgradeToProButton"
                    style={{ marginLeft: 'auto' }}
                    variant="outlined"
                    onClick={() => openUpgradeToProModal({
                        source: "Settings Page - Upgrade to Pro Button",
                        tab: "team"
                    })}
                >
                    Upgrade
                </ButtonSymphony>
            </Banner>
        )
    }

    return (
        <PageSymphony
            className="membersPage"
            isContent
        >

            <CardSymphony
                className="organizationPageCard"
                title={shouldShowTeamUsers ? `Team Access to ${currentBrand?.name}` : "Members"}
                subtitle={shouldShowTeamUsers 
                    ? "Manage which team members have access to this brand."
                    : "Invite other users to your brand."
                }
            >
                <div className="usersTabComponent" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {canViewTeamMembers ? (
                        <>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {canInviteMembers && (
                                    <FeatureGate
                                        feature={FeatureKeys.TEAM_MEMBER_INVITE}
                                        upgradeCallToAction="Invite your team with Symphony Growth"
                                        upgradeSource="Settings - Invite Member"
                                    >
                                        <ButtonSymphony
                                            className="membersPageInviteMemberButton"
                                            onClick={handleClickAddNewMemberButton}
                                            iconLeft="gem"
                                            iconLeftSize={22}
                                            iconLeftOffset={-2}
                                        >
                                            Invite a Member
                                        </ButtonSymphony>
                                    </FeatureGate>
                                )}
                        
                                {/* Only show "Start a Team Account" if user doesn't have an organization */}
                                {!shouldHideStartTeamButton && (
                                    <ButtonSymphony
                                        className="membersPageStartTeamButton"
                                        onClick={handleStartTeamAccountButton}
                                        iconLeft="gem"
                                        iconLeftSize={22}
                                        iconLeftOffset={-2}
                                    >
                                        Start a Team Account
                                    </ButtonSymphony>
                                )}
                            </div>
                            {renderUsersTable()}
                        </>
                    ) : (
                        <Banner
                            className="membersPageViewOnlyBanner"
                            title='Members with "View Only" access cannot see team members'
                            description="Ask your team admin to give you full admin access to this brand to view and manage team members."
                            color="yellow"
                        />
                    )}
                </div>

                {/* MODALS */}
                {renderDeleteUserModal()}
                {renderAddUserModal()}
                
                {/* Team context modals */}
                <AddMemberModal
                    isOpen={showAddMemberModal}
                    onClose={() => {
                        setShowAddMemberModal(false);
                        // Note: organizationUsers state is automatically updated by the AddMemberModal
                        // via useOrganizationV2.inviteOrganizationUsers, so no manual refresh needed
                    }}
                />
                
                <EditBrandUserPermissionsModal
                    isOpen={showEditBrandPermissionsModal}
                    onClose={() => {
                        setShowEditBrandPermissionsModal(false);
                        setUserToEdit(null);
                    }}
                    selectedUser={userToEdit}
                />

                {/* <AddUserModal
                {...{ brandId }}
                users={pulledUsers.users}
                hasReachedLimit={hasReachedLimit}
                open={addUserModal.open}
                onClose={handleCloseAddModal}
                onSaveUsers={setPulledUsers}
            /> */}
            </CardSymphony>
        </PageSymphony>
    )
}

export default MembersPage
